<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <!-- handles camera stream, switching between front/back camera,
    displays live preview, captures actual image, emits photo + metadata -->
  <teleport to="body">
    <div
      class="fixed inset-0 z-100000 bg-black/95 flex flex-col overflow-hidden">
      <!-- Close Button -->
      <button @click="handleClose" class="camera-close-btn camera-btn z-50">
        <X class="w-6 h-6" />
      </button>

      <!-- Info Button -->
      <button
        v-if="ui.showInfo"
        @click="toggleInfo"
        class="camera-info-btn camera-btn z-50">
        <Info class="w-6 h-6" />
      </button>

      <!-- Info Popup -->
      <transition name="fade">
        <div
          v-if="showInfoPopup"
          class="camera-info-popup z-50 w-64 bg-[var(--color-primary-100)] border border-[var(--color-primary-400)] rounded-xl shadow-xl p-4 text-sm text-[var(--color-text)] backdrop-blur-md">
          <div class="flex justify-between items-center mb-2">
            <span class="font-semibold text-[var(--color-primary-900)]">
              {{ $t('views.camera.title') }}
            </span>
            <X class="w-4 h-4 cursor-pointer" @click="showInfoPopup = false" />
          </div>
          <p class="leading-snug">{{ $t('pageInfo.cameraCapture') }}</p>
        </div>
      </transition>

      <!-- Switch Camera -->
      <button @click="switchCamera" class="camera-switch-btn camera-btn z-50">
        <SwitchCamera class="w-6 h-6" />
      </button>

      <!-- Camera Area -->
      <div
        class="relative flex-1 overflow-hidden bg-black flex items-center justify-center">
        <div :style="squareContainerStyle">
          <video
            ref="video"
            autoplay
            playsinline
            muted
            :style="videoStyle"
            class="w-full h-full" />
        </div>
        <canvas ref="canvas" class="hidden"></canvas>

        <div
          v-if="!cameraReady"
          class="absolute inset-0 flex items-center justify-center text-white z-10">
          <p>{{ $t('views.camera.loading') }}</p>
        </div>

        <!-- Capture Button -->
        <div class="camera-capture-wrapper z-50">
          <button
            @click="capture"
            :disabled="!cameraReady"
            class="camera-btn camera-btn--capture disabled:opacity-50">
            <Camera class="w-8 h-8" />
          </button>
        </div>

        <!-- Gallery Button -->
        <div class="camera-gallery-btn z-50">
          <label class="camera-btn cursor-pointer">
            <input
              type="file"
              accept="image/*"
              @change="handleGalleryUpload"
              class="hidden" />
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </label>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
  import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
  import { X, Camera, SwitchCamera, Info } from 'lucide-vue-next'
  import { useUiStore } from '@/stores/ui.store'
  // emit captured photo and metadata
  const emit = defineEmits(['close', 'photo'])

  const ui = useUiStore()
  const showInfoPopup = ref(false)
  const toggleInfo = () => (showInfoPopup.value = !showInfoPopup.value)

  const video = ref(null)
  const canvas = ref(null)
  const stream = ref(null)
  const cameraReady = ref(false)
  const facingMode = ref('environment')
  const isCancelled = ref(false)
  const squareContainerStyle = computed(() => ({
    width: 'min(100vw, 100vh)',
    height: 'min(100vw, 100vh)',
    aspectRatio: '1 / 1',
    overflow: 'hidden',
  }))

  const videoStyle = computed(() => ({
    objectFit: 'cover',
    objectPosition: 'center',
  }))

  async function startCamera() {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facingMode.value },
      })

      // Check if component was cancelled while waiting for camera
      if (isCancelled.value) {
        mediaStream.getTracks().forEach((track) => track.stop())
        return
      }

      stream.value = mediaStream
      video.value.srcObject = mediaStream

      await new Promise((resolve) => {
        video.value.onloadedmetadata = () => {
          // Check again before setting up video
          if (isCancelled.value) {
            mediaStream.getTracks().forEach((track) => track.stop())
            resolve()
            return
          }
          video.value.play()
          cameraReady.value = true
          resolve()
        }
      })
    } catch (err) {
      if (!isCancelled.value) {
        alert('Camera access denied or unavailable.')
        emit('close')
      }
    }
  }

  function stopCamera() {
    stream.value?.getTracks().forEach((t) => t.stop())
    stream.value = null
  }

  async function switchCamera() {
    if (isCancelled.value) return

    facingMode.value =
      facingMode.value === 'environment' ? 'user' : 'environment'
    stopCamera()
    cameraReady.value = false
    await nextTick()

    if (!isCancelled.value) {
      startCamera()
    }
  }

  async function capture() {
    const can = canvas.value
    const vid = video.value
    const ctx = can.getContext('2d')

    // Calculate square crop from center of video
    const videoWidth = vid.videoWidth
    const videoHeight = vid.videoHeight
    const squareSize = Math.min(videoWidth, videoHeight)

    // Calculate crop offsets to center the square
    const cropX = (videoWidth - squareSize) / 2
    const cropY = (videoHeight - squareSize) / 2

    // Set canvas to square size
    can.width = squareSize
    can.height = squareSize

    // Draw cropped square image from center of video
    ctx.drawImage(
      vid,
      cropX,
      cropY,
      squareSize,
      squareSize, // source rectangle (crop from video)
      0,
      0,
      squareSize,
      squareSize // destination rectangle (canvas)
    )

    const photoData = can.toDataURL('image/jpeg', 0.9)

    const metadata = {
      width: squareSize,
      height: squareSize,
      focalLength: await getFocalLengthEstimate(),
    }

    emit('photo', { photoData, metadata })
    emit('close')
  }

  async function handleGalleryUpload(event) {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      // read original file to preserve EXIF data
      const originalFileBlob = file.slice()

      // Read file as data URL
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          // Crop to square
          const can = canvas.value
          const ctx = can.getContext('2d')

          const imgWidth = img.width
          const imgHeight = img.height
          const squareSize = Math.min(imgWidth, imgHeight)

          const cropX = (imgWidth - squareSize) / 2
          const cropY = (imgHeight - squareSize) / 2

          can.width = squareSize
          can.height = squareSize

          ctx.drawImage(
            img,
            cropX,
            cropY,
            squareSize,
            squareSize,
            0,
            0,
            squareSize,
            squareSize
          )

          const photoData = can.toDataURL('image/jpeg', 0.9)

          const metadata = {
            width: squareSize,
            height: squareSize,
            focalLength: null,
            source: 'gallery',
            originalFile: originalFileBlob,
          }

          emit('photo', { photoData, metadata })
          emit('close')
        }
        img.src = e.target.result
      }
      reader.readAsDataURL(file)
    } catch (err) {
      console.error('Failed to load image from gallery:', err)
      alert('Failed to load image from gallery')
    }
  }

  async function getFocalLengthEstimate() {
    try {
      const track = stream.value?.getVideoTracks()[0]
      if (!track) return null

      // getSettings first (more likely to have the active value)
      const settings = track.getSettings()
      if (settings?.focalLength) return settings.focalLength

      // Fallback to capabilities
      const caps = track.getCapabilities?.()
      if (caps?.focalLength) {
        // range, we don't know the exact value without specialized APIs,
        // for fixed lenses it's a number.
        return typeof caps.focalLength === 'number'
          ? caps.focalLength
          : caps.focalLength.min || null
      }
    } catch (e) {
      // ignore and fallback
    }
    return null
  }

  function handleClose() {
    // set cancellation flag to prevent any ongoing camera operations
    isCancelled.value = true
    cameraReady.value = false

    // Stop current stream
    stopCamera()

    // stop any pending camera initialization
    if (video.value && video.value.srcObject) {
      const tracks = video.value.srcObject.getTracks()
      tracks.forEach((track) => track.stop())
      video.value.srcObject = null
    }

    emit('close')
  }

  onMounted(() => {
    document.body.style.overflow = 'hidden'
    startCamera()
  })
  onUnmounted(() => {
    isCancelled.value = true
    document.body.style.overflow = ''
    stopCamera()
  })
</script>
